/**
 * Tracks a broadcast started by admin prepare before any peer connects.
 *
 * Admin prepare publishes Hosting as soon as the host is listening. If the
 * admin peer never connects, nothing else clears that status. This guard
 * remembers that the broadcast is still unclaimed and fires once if nobody
 * claims it.
 */
export class AdminPrepareBroadcastGuard {
  private unclaimed = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private releasedAttempt = 0;

  constructor(
    private readonly timeoutMs: number,
    private readonly onTimeout: () => void,
  ) {}

  /**
   * This call owns the broadcast. Clears any previous deadline so an old
   * timeout cannot release the new attempt while start is still in flight.
   */
  public beginAttempt(): number {
    this.attempt += 1;
    this.unclaimed = true;
    this.clearTimer();
    return this.attempt;
  }

  public isUnclaimed(): boolean {
    return this.unclaimed;
  }

  public wasReleased(attempt: number): boolean {
    return attempt !== 0 && this.releasedAttempt === attempt;
  }

  public armTimeout(): void {
    if (!this.unclaimed) return;
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.onTimeout();
    }, this.timeoutMs);
  }

  /**
   * Nobody joined. The in-flight start must not publish this attempt after
   * the peer id arrives.
   */
  public releaseCurrentAttempt(): void {
    if (!this.unclaimed) return;
    this.releasedAttempt = this.attempt;
    this.claim();
  }

  /** A peer connected, or this broadcast is no longer an admin-prepare attempt. */
  public claim(): void {
    this.unclaimed = false;
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
